import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  MousePointer, 
  CheckCircle, 
  CreditCard, 
  Flag,
  ArrowRight,
  AlertTriangle
} from 'lucide-react';

const steps = [
  {
    number: 1,
    title: 'Elige cuántas participaciones deseas',
    description: 'Selecciona la cantidad de números que deseas adquirir. El sistema asigna automáticamente números disponibles y los registra a tu nombre, garantizando transparencia e imparcialidad en el proceso.',
    icon: MousePointer,
    color: 'bg-blue-500'
  },
  {
    number: 2,
    title: 'Confirma tu participación',
    description: 'Revisa los detalles de tu selección y confirma tu participación en el evento activo.',
    icon: CheckCircle,
    color: 'bg-emerald-500'
  },
  {
    number: 3,
    title: 'Completa el pago',
    description: 'Realiza el pago a través de nuestros métodos disponibles: Bre-B o Bancolombia. Envía tu comprobante por WhatsApp.',
    icon: CreditCard,
    color: 'bg-purple-500'
  },
  {
    number: 4,
    title: 'Cierre del evento',
    description: 'El evento se cierra conforme a las condiciones publicadas. Los resultados se publican en la plataforma.',
    icon: Flag,
    color: 'bg-amber-500'
  }
];

export default function HowToParticipatePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero */}
      <div className="bg-gradient-to-r from-slate-700 to-slate-800 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            ¿Cómo funcionaba?
          </h1>
          <p className="text-slate-300 text-lg">
            Así era el proceso de participación en TuPrima (temporalmente suspendido)
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="space-y-6">
          {steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <Card key={step.number} className="overflow-hidden" data-testid={`step-${step.number}`}>
                <CardContent className="p-0">
                  <div className="flex items-stretch">
                    {/* Number indicator */}
                    <div className={`${step.color} w-20 flex-shrink-0 flex flex-col items-center justify-center text-white`}>
                      <span className="text-3xl font-bold">{step.number}</span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-full ${step.color} bg-opacity-10 flex items-center justify-center`}>
                          <Icon className={`w-5 h-5 ${step.color.replace('bg-', 'text-')}`} />
                        </div>
                        <h3 className="font-bold text-lg text-slate-800">{step.title}</h3>
                      </div>
                      <p className="text-slate-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Connector lines between steps */}
        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 -z-10" />

        {/* CTA Section */}
        <div className="mt-12 text-center">
          <div className="bg-gradient-to-r from-slate-100 to-slate-50 rounded-2xl p-8 border border-slate-200">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
            <h2 className="text-2xl font-bold text-slate-800 mb-3">
              Plataforma Temporalmente Suspendida
            </h2>
            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              Actualmente no estamos recibiendo nuevas participaciones. 
              Si ya compraste tickets, puedes solicitar tu devolución.
            </p>
            <Link to="/">
              <Button 
                size="lg" 
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8 h-14 text-lg"
                data-testid="go-home-btn"
              >
                Volver al Inicio
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <Card className="bg-blue-50 border-blue-100">
            <CardContent className="p-6">
              <h4 className="font-bold text-blue-900 mb-2">Números únicos</h4>
              <p className="text-blue-800 text-sm">
                Cada número es único y queda registrado digitalmente. No hay duplicados.
              </p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-100">
            <CardContent className="p-6">
              <h4 className="font-bold text-purple-900 mb-2">Pago seguro</h4>
              <p className="text-purple-800 text-sm">
                Utilizamos métodos de pago confiables con confirmación manual verificada.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
